import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import GlobalStyles from './styles/GlobalStyles';

// Auth Components
import Login from './components/auth/Login';
import SetPassword from './components/auth/SetPassword';

// Layout Components
import Layout from './components/common/Layout';

// Dashboard Components
import Dashboard from './components/dashboard/Dashboard';

// Projects Components
import ProjectsList from './components/projects/ProjectsList';
import ProjectDetail from './components/projects/ProjectDetail';
import ProjectForm from './components/projects/ProjectForm';

// Funds Components
import FundRequestsList from './components/funds/FundRequestsList';
import FundRequestDetail from './components/funds/FundRequestDetail';
import FundRequestForm from './components/funds/FundRequestForm';

// Accounting Components
import AccountingList from './components/accounting/AccountingList';
import PaymentForm from './components/accounting/PaymentForm';
import PaymentDetail from './components/accounting/PaymentDetail';
import ProjectFinancialDetail from './components/accounting/ProjectFinancialDetail';
import ExpenseDetail from './components/accounting/ExpenseDetail';
import AccountingEntryForm from './components/accounting/AccountingEntryForm';

// Reports Components
import ReportsList from './components/reports/ReportsList';
import ReportGenerator from './components/reports/ReportGenerator';

// Users Components
import UsersList from './components/users/UsersList';
import UserDetail from './components/users/UserDetail';
import UserForm from './components/users/UserForm';

// Office Components
import FinanceList from './components/office/FinanceList';
import FinanceDetail from './components/office/FinanceDetail';
import FinanceForm from './components/office/FinanceForm';
import Budget from './components/office/Budget';

import OfficeFinancialSummary from './components/office/OfficeFinancialSummary';
import LoansList from './components/office/LoansList';
import LoanForm from './components/office/LoanForm';
import LoanDetail from './components/office/LoanDetail';
import LoanPayment from './components/office/LoanPayment';
import OfficeBudgetApproval from './components/office/OfficeBudgetApproval';

// Notifications Components
import NotificationsList from './components/notifications/NotificationsList';

// Logs Components
import LogsList from './components/logs/LogsList';

// Test Components
import TestNotifications from './components/TestNotifications';

// Cashier Components
import CashierLayout from './components/cashier/CashierLayout';
import CashierDashboard from './components/cashier/CashierDashboard';
import CashierExpenseForm from './components/cashier/CashierExpenseForm';
import CashierExpensesList from './components/cashier/CashierExpensesList';
import CashierExpenseDetail from './components/cashier/CashierExpenseDetail';
import CashierFundRequests from './components/cashier/CashierFundRequests';
import CashierReports from './components/cashier/CashierReports';

// Project Manager Components
import ManagerLayout from './components/manager/ManagerLayout';
import ManagerDashboard from './components/manager/ManagerDashboard';
import ManagerFundRequestDetail from './components/manager/FundRequestDetail';
import ManagerProjectsList from './components/manager/ManagerProjectsList';
import ManagerFundRequestsList from './components/manager/ManagerFundRequestsList';
import ManagerProjectDetail from './components/manager/ManagerProjectDetail';
import ManagerProjectForm from './components/manager/ManagerProjectForm';
import ManagerProfile from './components/manager/ManagerProfile';
import ManagerSettings from './components/manager/ManagerSettings';

// Supervisor Components
import SupervisorLayout from './components/supervisor/SupervisorLayout';
import SupervisorDashboard from './components/supervisor/SupervisorDashboard';
import SupervisorFundRequestsList from './components/supervisor/SupervisorFundRequestsList';
import SupervisorFundRequestDetail from './components/supervisor/SupervisorFundRequestDetail';
import SupervisorFundRequestForm from './components/supervisor/SupervisorFundRequestForm';
import SupervisorAccounting from './components/supervisor/SupervisorAccounting';
import SupervisorProjectDetail from './components/supervisor/SupervisorProjectDetail';
import SupervisorProfile from './components/supervisor/SupervisorProfile';
import SupervisorSettings from './components/supervisor/SupervisorSettings';
import SupervisorNotifications from './components/supervisor/SupervisorNotifications';

// HR Components
import HRLayout from './components/hr/HRLayout';
import HRDashboard from './components/hr/HRDashboard';
import HRProjectsList from './components/hr/HRProjectsList';
import HRProjectDetail from './components/hr/HRProjectDetail';
import HRStaffList from './components/hr/HRStaffList';
import HRStaffDetail from './components/hr/HRStaffDetail';
import HRProfile from './components/hr/HRProfile';
import HRSettings from './components/hr/HRSettings';
import HRProjectForm from './components/hr/HRProjectForm';

// Admin Components
import AdminProfile from './components/admin/AdminProfile';
import AdminSettings from './components/admin/AdminSettings';
import AdminApprovals from './components/admin/AdminApprovals';
import AdminExpenseApproval from './components/admin/AdminExpenseApproval';

// Accountant Components
import AccountantLayout from './components/accountant/AccountantLayout';
import AccountantDashboard from './components/accountant/AccountantDashboard';
import AccountantProjectsList from './components/accountant/AccountantProjectsList';
import AccountantFundRequestsList from './components/accountant/AccountantFundRequestsList';
import AccountantAccountingList from './components/accountant/AccountantAccountingList';
import AccountantOfficeManagement from './components/accountant/AccountantOfficeManagement';
import AccountantReportsList from './components/accountant/AccountantReportsList';

// Store Keeper Components
import StoreKeeperLayout from './components/storekeeper/StoreKeeperLayout';
import StoreKeeperDashboard from './components/storekeeper/StoreKeeperDashboard';
import StoreKeeperOfficeManagement from './components/storekeeper/StoreKeeperOfficeManagement';
import StoreKeeperBudget from './components/storekeeper/StoreKeeperBudget';
import StoreKeeperLoansList from './components/storekeeper/StoreKeeperLoansList';
import StoreKeeperProfile from './components/storekeeper/StoreKeeperProfile';
import StoreKeeperSettings from './components/storekeeper/StoreKeeperSettings';
import StoreKeeperExpenseTracking from './components/storekeeper/StoreKeeperExpenseTracking';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

// HR Protected Route
const HRProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user || user.role !== 'HR (Admin)') return <Navigate to="/login" />;
  return <>{children}</>;
};

// Add AdminProtectedRoute
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user || user.email !== 'admin@example.com') return <Navigate to="/login" />;
  return <>{children}</>;
};

// Accountant Protected Route
const AccountantProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user || user.role !== 'Accountant') return <Navigate to="/login" />;
  return <>{children}</>;
};

// Store Keeper Protected Route
const StoreKeeperProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user || user.role !== 'Store-Keeper (Logistics)') return <Navigate to="/login" />;
  return <>{children}</>;
};

// Conditional Dashboard Component
const ConditionalDashboard: React.FC = () => {
  const { user } = useAuth();
  
  // Always show the regular Dashboard for all users
  return <Dashboard />;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <GlobalStyles />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password/:token" element={<SetPassword />} />
          
          {/* Main Application Routes (with Layout) */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<ConditionalDashboard />} />
            <Route path="dashboard" element={<ConditionalDashboard />} />
            
            {/* Project Routes */}
            <Route path="projects" element={<ProjectsList />} />
            <Route path="projects/new" element={<ProjectForm />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="projects/edit/:id" element={<ProjectForm />} />
            
            {/* Fund Request Routes */}
            <Route path="funds" element={<FundRequestsList />} />
            <Route path="funds/new" element={<FundRequestForm />} />
            <Route path="funds/:id" element={<FundRequestDetail />} />
            <Route path="funds/edit/:id" element={<FundRequestForm />} />
            
            {/* Accounting Routes */}
            <Route path="accounting" element={<AccountingList />} />
            <Route path="accounting/payment/new" element={<PaymentForm />} />
            <Route path="accounting/payment/:id" element={<PaymentDetail />} />
            <Route path="accounting/payment/edit/:id" element={<PaymentForm />} />
            <Route path="accounting/expense/:expenseId" element={<ExpenseDetail />} />
            <Route path="accounting/project/:projectId" element={<ProjectFinancialDetail />} />
            <Route path="accounting/entry/new" element={<AccountingEntryForm />} />
            <Route path="accounting/entry/:id" element={<AccountingEntryForm />} />

            
            {/* Reports Routes */}
            <Route path="reports" element={<ReportsList />} />
            <Route path="reports/:reportType" element={<ReportGenerator />} />
            
            {/* User Management Routes */}
            <Route path="users" element={<UsersList />} />
            <Route path="users/new" element={<UserForm />} />
            <Route path="users/:id" element={<UserDetail />} />
            <Route path="users/edit/:id" element={<UserForm />} />
            
            {/* Office Management Routes */}
            <Route path="office" element={<FinanceList />} />
            <Route path="office/finance/:id" element={<FinanceDetail />} />
            <Route path="office/finance/new" element={<FinanceForm />} />
            <Route path="office/finance/edit/:id" element={<FinanceForm />} />
            <Route path="office/budget" element={<Budget />} />
            <Route path="office/budget-approval" element={<OfficeBudgetApproval />} />

            <Route path="office/summary" element={<OfficeFinancialSummary />} />
            <Route path="office/loans" element={<LoansList />} />
            <Route path="office/loans/new" element={<LoanForm />} />
            <Route path="office/loans/edit/:id" element={<LoanForm />} />
            <Route path="office/loans/:id" element={<LoanDetail />} />
            <Route path="office/loans/:id/payment" element={<LoanPayment />} />
            
            {/* Notifications Routes */}
            <Route path="notifications" element={<NotificationsList />} />
            
            {/* Logs Routes */}
            <Route path="logs" element={<LogsList />} />
            
            {/* Settings Routes */}
            <Route path="settings" element={<AdminSettings />} />
            
            {/* Add more routes as components are created */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Route>
          
          {/* Cashier Routes (Standalone - No Layout) */}
          <Route path="cashier" element={
            <ProtectedRoute>
              <CashierLayout />
            </ProtectedRoute>
          }>
            <Route index element={<CashierDashboard />} />
            <Route path="expenses" element={<CashierExpensesList />} />
            <Route path="expense/:id" element={<CashierExpenseDetail />} />
            <Route path="expense/new" element={<CashierExpenseForm />} />
            <Route path="expense/edit/:id" element={<CashierExpenseForm />} />
            <Route path="fund-requests" element={<CashierFundRequests />} />
            <Route path="reports" element={<CashierReports />} />
          </Route>
          
          {/* Project Manager Routes (Standalone - No Layout) */}
          <Route path="manager" element={
            <ProtectedRoute>
              <ManagerLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ManagerDashboard />} />
            <Route path="projects" element={<ManagerProjectsList />} />
            <Route path="projects/:id" element={<ManagerProjectDetail />} />
            <Route path="projects/:id/edit" element={<ManagerProjectForm />} />
            <Route path="projects/new" element={<ManagerProjectForm />} />
            <Route path="fund-requests" element={<ManagerFundRequestsList />} />
            <Route path="fund-requests/:id" element={<ManagerFundRequestDetail />} />
            <Route path="fund-requests/:id/approve" element={<ManagerFundRequestDetail />} />
            <Route path="reports" element={<ReportsList />} />
            <Route path="reports/:reportType" element={<ReportGenerator />} />
            <Route path="team" element={<UsersList />} />
            <Route path="profile" element={<ManagerProfile />} />
            <Route path="settings" element={<ManagerSettings />} />
          </Route>

          {/* Supervisor Routes (Standalone - No Layout) */}
          <Route path="supervisor" element={
            <ProtectedRoute>
              <SupervisorLayout />
            </ProtectedRoute>
          }>
            <Route index element={<SupervisorDashboard />} />
            <Route path="project" element={<SupervisorProjectDetail />} />
            <Route path="funds" element={<SupervisorFundRequestsList />} />
            <Route path="funds/:id" element={<SupervisorFundRequestDetail />} />
            <Route path="funds/new" element={<SupervisorFundRequestForm />} />
            <Route path="accounting" element={<SupervisorAccounting />} />
            <Route path="notifications" element={<SupervisorNotifications />} />
            <Route path="profile" element={<SupervisorProfile />} />
            <Route path="settings" element={<SupervisorSettings />} />
          </Route>

          {/* HR (Admin) Portal Routes (Standalone - No Layout) */}
          <Route path="hr" element={
            <HRProtectedRoute>
              <HRLayout />
            </HRProtectedRoute>
          }>
            <Route index element={<HRDashboard />} />
            <Route path="projects" element={<HRProjectsList />} />
            <Route path="projects/:id" element={<HRProjectDetail />} />
            <Route path="projects/edit/:id" element={<HRProjectForm />} />
            <Route path="staff" element={<HRStaffList />} />
            <Route path="staff/:id" element={<HRStaffDetail />} />
            <Route path="profile" element={<HRProfile />} />
            <Route path="settings" element={<HRSettings />} />
          </Route>

          {/* Admin Routes (Standalone - No Layout) */}
          <Route path="admin" element={
            <AdminProtectedRoute>
              <Layout />
            </AdminProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="approvals" element={<AdminApprovals />} />
            <Route path="expense-approval" element={<AdminExpenseApproval />} />
          </Route>

          {/* Accountant Portal Routes (Standalone - No Layout) */}
          <Route path="/accountant" element={
            <AccountantProtectedRoute>
              <AccountantLayout />
            </AccountantProtectedRoute>
          }>
            <Route index element={<Navigate to="/accountant/dashboard" />} />
            <Route path="dashboard" element={<AccountantDashboard />} />
            <Route path="projects" element={<AccountantProjectsList />} />
            <Route path="funds" element={<AccountantFundRequestsList />} />
            <Route path="accounting" element={<AccountantAccountingList />} />
            <Route path="office" element={<AccountantOfficeManagement />} />
            <Route path="reports" element={<AccountantReportsList />} />
          </Route>

          {/* Store Keeper Portal Routes (Standalone - No Layout) */}
          <Route path="storekeeper" element={
            <StoreKeeperProtectedRoute>
              <StoreKeeperLayout />
            </StoreKeeperProtectedRoute>
          }>
            <Route index element={<Navigate to="/storekeeper/dashboard" />} />
            <Route path="dashboard" element={<StoreKeeperDashboard />} />
            <Route path="office" element={<StoreKeeperOfficeManagement />} />
            <Route path="office/budget" element={<StoreKeeperBudget />} />
            <Route path="office/expenses" element={<StoreKeeperExpenseTracking />} />
            <Route path="office/loans" element={<StoreKeeperLoansList />} />
            <Route path="profile" element={<StoreKeeperProfile />} />
            <Route path="settings" element={<StoreKeeperSettings />} />
          </Route>

          {/* Test Routes */}
          <Route path="test-notifications" element={
            <ProtectedRoute>
              <TestNotifications />
            </ProtectedRoute>
          } />
        </Routes>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
};

export default App; 